import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import { Checkbox, ListItemIcon, ListItemText, TextField, ListItemButton, Paper, Box, useTheme } from '@mui/material'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import { common } from '@mui/material/colors'

import { get, noop } from 'lodash'
import RenderFilterOption from './RenderFilterOption'

const WIDTH = 200

const CommonFilterMenu = ({
  value,
  onChange = noop,
  renderOption = RenderFilterOption,
  label,
  options = [],
  boxRestProps = {},
  onClose = noop,
  ...rest
}) => {
  const anchorEl = get(rest, 'anchorEl')
  const isOpen = Boolean(anchorEl)
  const dropdownWidth = get(rest, 'width', WIDTH)
  const theme = useTheme()
  const idList = useMemo(() => options.map(({ id }) => id), [options])
  const selectedOptions = useMemo(() => value || [], [value])
  const allSelected = useMemo(() => selectedOptions.length === idList.length, [selectedOptions, idList])

  const getOptionLabel = option => get(option, 'value')

  const toggleAllSelection = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      const newSelected = allSelected ? [] : [...options]
      onChange(newSelected)
    },
    [allSelected, options]
  )

  const handleChange = (event, newSelected) => {
    onChange(newSelected)
  }

  const SelectAllPaperComponent = useMemo(
    () => paperProps => {
      const { children, ...restPaperProps } = paperProps
      return (
        <Paper square sx={{ width: dropdownWidth }} {...restPaperProps}>
          <ListItemButton disableGutters divider selected={allSelected} onMouseDown={toggleAllSelection}>
            <ListItemIcon sx={{ minWidth: 0 }}>
              <Checkbox checked={allSelected} />
            </ListItemIcon>
            <ListItemText primary={allSelected ? 'Deselect all' : 'Select all'} />
          </ListItemButton>
          {children}
        </Paper>
      )
    },
    [allSelected, toggleAllSelection]
  )

  const isOptionEqualToValue = (option, value) => {
    const optionId = get(option, 'id')
    const valueId = get(value, 'id')
    return optionId && optionId === valueId
  }

  const handleClose = event => {
    if (event?.type === 'mousedown') return
    onClose(event)
  }

  return (
    isOpen && (
      <Box
        position="absolute"
        top={50}
        right={20}
        width={dropdownWidth}
        bgcolor={common.white}
        borderRadius={1}
        border="1px solid"
        borderColor="divider"
        zIndex={theme.zIndex.tooltip}
        {...boxRestProps}
      >
        <Autocomplete
          multiple
          options={options}
          fullWidth
          slotProps={{
            paper: {
              allSelected,
              toggleAllSelection,
              square: true,
            },

            popper: {
              sx: { zIndex: theme.zIndex.tooltip },
            },

            listbox: {
              sx: { py: 0, [`& .${autocompleteClasses.option}`]: { pl: 0 } },
            },
          }}
          open={isOpen}
          onClose={handleClose}
          disableCloseOnSelect
          disableClearable
          onChange={handleChange}
          renderTags={() => <></>}
          value={selectedOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          renderOption={renderOption}
          renderInput={params => <TextField {...params} autoFocus variant="standard" fullWidth />}
          style={{ width: '100%' }}
          {...rest}
          slots={{
            paper: SelectAllPaperComponent,
          }}
        />
      </Box>
    )
  )
}

CommonFilterMenu.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  renderOption: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.array,
  boxRestProps: PropTypes.object,
  onClose: PropTypes.func,
}

export default CommonFilterMenu
